<script setup lang="ts">
import { useCurrencyFormatter } from '~/shared-kernel/presentation/composables/useCurrencyFormatter'
import { formatBusinessDate } from '~/shared-kernel/domain/business-time'
export interface PublicPurchaseCartDisplayLine {
  key: string
  label: string
  detail: string
  quantity: number
  isAssignedUnit?: boolean
  amount?: number
  currency?: string
  minQuantity?: number
  maxQuantity?: number
  quantityStep?: number
}

export interface PublicPurchaseCartDisplayEvent {
  key: string
  eventId: string
  occurrenceId: string
  name: string
  startsAt?: string
  /** Event page; when set the name links there so the shopper can keep choosing. */
  href?: string
  expiresAt?: string
  renewalAvailable?: boolean
  lines: PublicPurchaseCartDisplayLine[]
}

const props = defineProps<{
  events: PublicPurchaseCartDisplayEvent[]
  synchronizing: boolean
  /** Renders each event as its own card with a subtotal (floating cart); the purchase screen keeps the flat list. */
  cards?: boolean
}>()

const emit = defineEmits<{
  remove: [selection: { eventId: string; occurrenceId: string; lineKey: string }]
  changeQuantity: [selection: { eventId: string; occurrenceId: string; lineKey: string; quantity: number }]
  renew: [selection: { eventId: string; occurrenceId: string }]
  clear: []
}>()

const { t, locale } = useI18n()
const { formatMoney } = useCurrencyFormatter()
// Releasing every hold cannot be undone, so it asks once inline before emitting.
const confirmingClear = shallowRef(false)
watch(() => props.events.length, (count) => { if (!count) confirmingClear.value = false })

function eventSubtotal(lines: PublicPurchaseCartDisplayLine[]) {
  const priced = lines.filter(line => line.amount !== undefined && line.currency)
  return priced.length ? formatMoney(priced.reduce((sum, line) => sum + line.amount!, 0), priced[0]!.currency!) : null
}

// A leaving line is taken out of flow so the rest can slide up; pin it where it was meanwhile.
function pinLeavingLine(element: Element) {
  const line = element as HTMLElement
  line.style.top = `${line.offsetTop}px`
  line.style.width = `${line.offsetWidth}px`
}
</script>

<template>
  <div class="public-purchase-cart" :class="{ 'public-purchase-cart--cards': props.cards }">
    <TqEmptyState
      v-if="!props.events.length"
      :show-mark="false"
      :title="t('public.purchase.cartEmptyTitle')"
      :description="t('public.purchase.cartEmpty')"
    />

    <TransitionGroup
      v-else
      tag="div"
      name="public-purchase-cart-event"
      class="public-purchase-cart__events"
      @before-leave="pinLeavingLine"
    >
      <section
        v-for="cartEvent in props.events"
        :key="cartEvent.key"
        class="public-purchase-cart__event"
        :data-cart-event="cartEvent.key"
        :data-expires-at="cartEvent.expiresAt"
      >
        <header>
          <div>
            <h3>
              <NuxtLink v-if="cartEvent.href" :to="cartEvent.href" class="public-purchase-cart__event-link">
                {{ cartEvent.name }}
                <UIcon name="i-lucide-arrow-up-right" aria-hidden="true" />
              </NuxtLink>
              <template v-else>{{ cartEvent.name }}</template>
            </h3>
            <span v-if="cartEvent.startsAt">{{ formatBusinessDate(cartEvent.startsAt, locale, { dateStyle: 'medium', timeStyle: 'short' }) }}</span>
            <span v-if="cartEvent.expiresAt" data-purchase-tour="reservation">{{ t('public.purchase.expiresAt', { time: formatBusinessDate(cartEvent.expiresAt, locale, { timeStyle: 'short' }) }) }}</span>
          </div>
          <TqButton
            compact
            v-if="cartEvent.renewalAvailable"
            :disabled="props.synchronizing"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            :data-renew-event="cartEvent.key"
            @click="emit('renew', { eventId: cartEvent.eventId, occurrenceId: cartEvent.occurrenceId })"
          >
            {{ t('public.purchase.renewHold') }}
          </TqButton>
        </header>
        <TransitionGroup tag="ul" name="public-purchase-cart-line" class="public-purchase-cart__lines" :aria-label="t('public.purchase.selectedLines')" @before-leave="pinLeavingLine">
          <li v-for="line in cartEvent.lines" :key="line.key" :data-cart-line="line.key">
            <div class="public-purchase-cart__description">
              <strong>{{ line.label }}</strong>
              <span>{{ line.detail }}</span>
            </div>
            <strong v-if="line.amount !== undefined && line.currency" class="public-purchase-cart__amount">{{ formatMoney(line.amount, line.currency) }}</strong>
            <div v-if="line.maxQuantity !== undefined" class="public-purchase-cart__quantity-control">
              <TqButton
                compact
                variant="ghost"
                icon="i-lucide-minus"
                :disabled="props.synchronizing || line.quantity <= (line.minQuantity ?? 1)"
                :aria-label="t('public.purchase.decrease')"
                :data-decrease-line="line.key"
                @click="emit('changeQuantity', {
                  eventId: cartEvent.eventId,
                  occurrenceId: cartEvent.occurrenceId,
                  lineKey: line.key,
                  quantity: line.quantity - (line.quantityStep ?? 1)
                })"
              />
              <strong>{{ line.quantity }}</strong>
              <TqButton
                compact
                variant="ghost"
                icon="i-lucide-plus"
                :disabled="props.synchronizing || line.quantity >= line.maxQuantity"
                :aria-label="t('public.purchase.increase')"
                :data-increase-line="line.key"
                @click="emit('changeQuantity', {
                  eventId: cartEvent.eventId,
                  occurrenceId: cartEvent.occurrenceId,
                  lineKey: line.key,
                  quantity: line.quantity + (line.quantityStep ?? 1)
                })"
              />
            </div>
            <span v-else-if="line.isAssignedUnit && line.quantity > 1" class="public-purchase-cart__quantity">× {{ line.quantity }}</span>
            <TqButton
              class="public-purchase-cart__remove"
              variant="ghost"
              compact
              icon="i-lucide-trash-2"
              :disabled="props.synchronizing"
              :icon-only-label="t('public.purchase.removeSelection', { label: line.label })"
              @click="emit('remove', { eventId: cartEvent.eventId, occurrenceId: cartEvent.occurrenceId, lineKey: line.key })"
            />
          </li>
        </TransitionGroup>
        <p v-if="props.cards && props.events.length > 1 && eventSubtotal(cartEvent.lines)" class="public-purchase-cart__subtotal">
          <span>{{ t('public.purchase.subtotal') }}</span>
          <strong>{{ eventSubtotal(cartEvent.lines) }}</strong>
        </p>
      </section>
    </TransitionGroup>

    <!-- Status lives below the lines so a reservation round-trip never shifts what the shopper is touching. -->
    <div v-if="props.synchronizing || props.events.length" class="public-purchase-cart__clear">
      <span v-if="props.synchronizing" class="public-purchase-cart__sync" role="status">
        <UIcon name="i-lucide-loader-circle" aria-hidden="true" />
        {{ t('public.purchase.reserving') }}
      </span>
      <template v-if="props.events.length && confirmingClear">
        <span>{{ t('public.purchase.clearCartConfirm') }}</span>
        <TqButton compact variant="ghost" @click="confirmingClear = false">{{ t('actions.cancel') }}</TqButton>
        <TqButton compact variant="danger" :disabled="props.synchronizing" data-clear-cart @click="confirmingClear = false; emit('clear')">{{ t('public.purchase.clearCartAction') }}</TqButton>
      </template>
      <TqButton v-else-if="props.events.length" compact variant="ghost" icon="i-lucide-trash-2" :disabled="props.synchronizing" @click="confirmingClear = true">{{ t('public.purchase.clearCart') }}</TqButton>
    </div>
  </div>
</template>

<style scoped>
.public-purchase-cart {
  display: grid;
  gap: 1rem;
  align-content: start;
}

.public-purchase-cart__clear {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
}

.public-purchase-cart__clear span {
  flex: 1 1 10rem;
  color: var(--tq-text-soft);
  font-size: 0.85rem;
}

.public-purchase-cart__sync {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  color: var(--tq-text-soft);
  font-size: 0.85rem;
  /* Fast confirmations finish before the notice shows, so it never flickers. */
  animation: public-cart-fade 200ms var(--tq-ease-soft) 300ms both;
}

.public-purchase-cart__sync :deep(.iconify) {
  animation: public-cart-spin 900ms linear infinite;
}

.public-purchase-cart__lines {
  position: relative;
  display: grid;
  gap: 0.7rem;
  padding: 0;
  margin: 0;
  list-style: none;
}

.public-purchase-cart-line-enter-active,
.public-purchase-cart-line-leave-active {
  transition: opacity 200ms var(--tq-ease-soft), transform 200ms var(--tq-ease-soft);
}

.public-purchase-cart-line-move { transition: transform 240ms var(--tq-ease-soft); }

.public-purchase-cart-line-enter-from,
.public-purchase-cart-line-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

/* Leaving lines stop taking space so the rest slide up instead of jumping. */
.public-purchase-cart-line-leave-active { position: absolute; inset-inline-start: 0; }

.public-purchase-cart__event {
  display: grid;
  gap: 0.65rem;
}

.public-purchase-cart__event + .public-purchase-cart__event {
  padding-top: 1rem;
  border-top: 1px solid var(--tq-border);
}

/* The group wrapper is invisible in the flat list; cards turn it into a positioned grid so leaving cards can collapse. */
.public-purchase-cart__events { display: contents; }

/*
 * Cards variant (floating cart): every inline edge sits on --public-cart-pad, set by the panel, so header, group
 * heading, line text, subtotal and footer share one left/right edge. Groups are full-bleed sections divided by
 * hairlines; lines are divider rows, never boxes inside boxes.
 */
.public-purchase-cart--cards { gap: 0; }
.public-purchase-cart--cards .public-purchase-cart__events { position: relative; display: grid; }

.public-purchase-cart--cards .public-purchase-cart__event {
  gap: 0;
  padding: 0 var(--public-cart-pad, 1rem);
  border-bottom: 1px solid var(--tq-border);
}

.public-purchase-cart--cards .public-purchase-cart__event + .public-purchase-cart__event { padding-top: 0; border-top: 0; }

.public-purchase-cart--cards .public-purchase-cart__event header {
  align-items: center;
  margin-inline: calc(-1 * var(--public-cart-pad, 1rem));
  padding: 0.85rem var(--public-cart-pad, 1rem);
  background: var(--tq-bg-muted);
}

.public-purchase-cart--cards .public-purchase-cart__lines li {
  padding: 0.75rem 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.public-purchase-cart--cards .public-purchase-cart__lines li + li { border-top: 1px solid var(--tq-border); }
.public-purchase-cart--cards .public-purchase-cart__clear { justify-content: flex-start; padding: 0.75rem var(--public-cart-pad); }
.public-purchase-cart--cards .public-purchase-cart__remove { border-color: transparent; background: transparent; }

.public-purchase-cart--cards .public-purchase-cart-event-enter-active,
.public-purchase-cart--cards .public-purchase-cart-event-leave-active {
  transition: opacity 240ms var(--tq-ease-soft), transform 240ms var(--tq-ease-soft);
}

.public-purchase-cart--cards .public-purchase-cart-event-move { transition: transform 260ms var(--tq-ease-soft); }

.public-purchase-cart--cards .public-purchase-cart-event-enter-from,
.public-purchase-cart--cards .public-purchase-cart-event-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem) scale(0.98);
}

.public-purchase-cart--cards .public-purchase-cart-event-leave-active { position: absolute; inset-inline-start: 0; }

.public-purchase-cart__event-link {
  color: inherit;
  text-decoration: none;
}

.public-purchase-cart__event-link :deep(.iconify) { margin-inline-start: 0.25rem; vertical-align: -0.1em; }

.public-purchase-cart__event-link:hover { color: var(--tq-accent-readable); text-decoration: underline; }
.public-purchase-cart__event-link:focus-visible { outline: 2px solid var(--tq-focus); outline-offset: 2px; border-radius: var(--tq-radius-sm); }

.public-purchase-cart__subtotal {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin: 0;
  padding: 0.75rem 0;
  border-top: 1px solid var(--tq-border);
  color: var(--tq-text-soft);
  font-size: 0.85rem;
}

.public-purchase-cart__subtotal strong { color: var(--tq-text); font-size: 1rem; }

.public-purchase-cart__event header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.public-purchase-cart__event header > div {
  display: grid;
  gap: 0.2rem;
}

.public-purchase-cart__event h3 {
  margin: 0;
  color: var(--tq-text);
  font-size: 1rem;
}

.public-purchase-cart__event header span {
  color: var(--tq-text-soft);
  font-size: 0.78rem;
}

.public-purchase-cart__lines li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-areas: "name amount remove" "detail detail remove";
  gap: 0.1rem 0.5rem;
  align-items: center;
  padding: 0.6rem 0.45rem 0.6rem 0.8rem;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-md);
  background: var(--tq-bg-elevated);
}

/* Name and detail join the line grid so the detail can run under the price. */
.public-purchase-cart__description { display: contents; }

.public-purchase-cart__lines span {
  color: var(--tq-text-soft);
}

.public-purchase-cart__description strong { grid-area: name; min-width: 0; font-size: 0.95rem; line-height: 1.35; overflow-wrap: anywhere; }
.public-purchase-cart__description span { grid-area: detail; min-width: 0; font-size: 0.78rem; line-height: 1.4; overflow-wrap: anywhere; }
.public-purchase-cart__amount {
  grid-area: amount;
  font-size: 0.95rem;
  line-height: 1.35;
  white-space: nowrap;
}
.public-purchase-cart__remove { grid-area: remove; }
.public-purchase-cart__quantity-control,
.public-purchase-cart__quantity { grid-column: 1 / -1; grid-row: 3; margin-top: 0.35rem; }

.public-purchase-cart__quantity {
  font-weight: 700;
}

.public-purchase-cart__quantity-control {
  display: flex;
  align-items: center;
  gap: 0.2rem;
}

.public-purchase-cart__quantity-control strong {
  min-width: 1.5rem;
  text-align: center;
}

@keyframes public-cart-spin {
  to { transform: rotate(360deg); }
}

@keyframes public-cart-fade {
  from { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .public-purchase-cart__sync,
  .public-purchase-cart__sync :deep(.iconify) { animation: none; }
  .public-purchase-cart-line-enter-active,
  .public-purchase-cart-line-leave-active,
  .public-purchase-cart-line-move { transition: none; }
  .public-purchase-cart--cards .public-purchase-cart-event-enter-active,
  .public-purchase-cart--cards .public-purchase-cart-event-leave-active,
  .public-purchase-cart--cards .public-purchase-cart-event-move { transition: none; }
}
</style>
